import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {AccessService} from '../utils/AccessService';
import {LocalStorage} from '../utils/LocalStorage';
import {PendingWorkApi} from '../api/pendingWorkApi';
import {useUserModules} from './UserModulesContext';

/**
 * Trabajo pendiente del usuario: qué quedó esperándolo y qué acaba de terminar de procesarse.
 *
 * Existe porque la Memoria procesa en asincrónico y hasta ahora no le devolvía el resultado a nadie:
 * la única pantalla que mostraba el avance (la tabla de Documentos) pollea sólo mientras está
 * montada, así que bastaba con navegar a otro lado para no enterarse nunca de que quedaron hechos
 * esperando revisión. Un hecho sin curar no entra al contexto del agente — la cola muda es una fuga
 * del valor de la Memoria, no un detalle.
 *
 * Se monta UNA sola vez, en `App.js` y dentro de `UserModulesProvider`: es el único lugar que no
 * remonta al navegar. Sus consumidores (el aviso, el badge del tab) leen de acá y NO hacen fetch
 * propio — la lección de las nueve pantallas que pedían `access/modules` por su cuenta.
 *
 * CADENCIA ADAPTATIVA. La extracción tarda minutos, así que pollear rápido todo el día sería
 * absurdo:
 *   - algo en vuelo (el propio payload lo dice) → cada 15s, que es cuando la respuesta puede cambiar
 *   - nada en vuelo                             → cada 5 minutos, más un refetch al volver a la ventana
 *   - pestaña oculta                            → apagado; se despierta con `visibilitychange`
 */

export const KIND_MEMORY_FACTS = 'memory_facts';
export const KIND_MEMORY_PROCESSING = 'memory_processing';
export const KIND_MEMORY_READY = 'memory_ready';

const POLL_ACTIVE_MS = 15 * 1000;
const POLL_IDLE_MS = 5 * 60 * 1000;
// Cache del último payload, keyed por usuario (mismo patrón que `dashbo_user_modules_cache`): evita
// que los badges parpadeen a 0 en cada F5 mientras vuelve el primer fetch.
const CACHE_KEY = 'dashbo_pending_work_cache';

function readCachedItems(userId) {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (!cached || String(cached.userId) !== String(userId)) return [];
        return Array.isArray(cached.items) ? cached.items : [];
    } catch {
        return [];
    }
}

function writeCachedItems(userId, items) {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({userId, items}));
    } catch { /* incógnito / storage lleno: se pierde el render optimista, nada más */ }
}

const EMPTY_ITEMS = [];

const PendingWorkContext = createContext({
    items: EMPTY_ITEMS,
    loaded: false,
    pendingFactsByClient: {},
    refresh: () => {},
    overridePendingFacts: () => {},
});

export function PendingWorkProvider({children}) {
    const {clientMemoryLevel, loaded: modulesLoaded} = useUserModules();
    const userId = LocalStorage.fetchUserId() || null;
    const isClientUser = LocalStorage.isClientUser();

    // Sin Memoria no hay nada que avisar (hoy es el único proveedor de items). El usuario del portal
    // nunca pollea: el curado es de la agencia. Se espera a `modulesLoaded` para no disparar un
    // request con el nivel todavía en su default OFF.
    const enabled = !!userId && !isClientUser && modulesLoaded
        && clientMemoryLevel !== AccessService.MEMORY_LEVEL_OFF;

    const [items, setItems] = useState(EMPTY_ITEMS);
    const [loaded, setLoaded] = useState(false);
    // ⚠️ El TERCER estado: «se intentó y no se pudo». Sin él, `loaded` en false significaba a la vez
    // «todavía no volvió» y «no volvió nunca», y la campana quedaba diciendo «Cargando…» hasta el
    // próximo tick —que llega a los 5 minutos— (C-knob3-09, review del 27/09).
    const [failed, setFailed] = useState(false);
    // Correcciones optimistas del conteo de cola por cliente (al curar, el badge cae en el acto sin
    // esperar al próximo poll). El poll siguiente las pisa con el número real.
    const [factOverrides, setFactOverrides] = useState({});

    const timerRef = useRef(null);
    const cancelledRef = useRef(false);
    // Un request a la vez (ver `fetchNow`).
    const inFlightRef = useRef(false);
    // El fetch se guarda en un ref para que los listeners (focus/visibilidad) llamen SIEMPRE a la
    // versión vigente sin re-suscribirse en cada render.
    const fetchRef = useRef(() => {});
    // Espejo de `items` para el poller: el efecto se monta una sola vez, así que leer el state desde
    // su closure daría el valor del primer render. Con el ref, decidir la cadencia tras un error de
    // red usa lo último que se supo de verdad.
    const itemsRef = useRef(EMPTY_ITEMS);
    itemsRef.current = items;

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        cancelledRef.current = false;
        // Si el efecto se rearma (cambio de usuario/nivel) con un request viejo todavía en vuelo, su
        // `.then` ya no cuenta: sin este reset el guard quedaría trabado y el poller mudo.
        inFlightRef.current = false;
        clearTimer();

        if (!enabled) {
            itemsRef.current = EMPTY_ITEMS;
            setItems(EMPTY_ITEMS);
            setFactOverrides({});
            setLoaded(modulesLoaded);
            return () => { cancelledRef.current = true; };
        }

        // Arranque optimista con lo último que vimos de ESTE usuario.
        const cached = readCachedItems(userId);
        itemsRef.current = cached;
        setItems(cached);

        // `clearTimer()` primero SIEMPRE: dos caminos pueden programar el próximo tick (el `.then` de
        // un fetch y el de otro disparado en paralelo), y sobrescribir `timerRef` sin limpiar deja
        // timers huérfanos que despiertan de más.
        const schedule = () => {
            clearTimer();
            if (cancelledRef.current || document.hidden) return;
            const inFlight = itemsRef.current.some((item) => item.kind === KIND_MEMORY_PROCESSING);
            timerRef.current = setTimeout(fetchRef.current, inFlight ? POLL_ACTIVE_MS : POLL_IDLE_MS);
        };

        const fetchNow = () => {
            clearTimer();
            // Un solo request en vuelo. Volver a la pestaña dispara `focus` Y `visibilitychange`:
            // sin este guard eran dos requests en paralelo por cada vuelta, en el único endpoint que
            // el frontend consulta en loop (review 2026-07-31).
            if (inFlightRef.current) return;
            inFlightRef.current = true;
            PendingWorkApi.getPendingWork().then((data) => {
                inFlightRef.current = false;
                if (cancelledRef.current) return;
                // Fail-soft: una respuesta rara (red caída, 500) deja lo que ya había y reintenta en
                // el próximo tick. Este poller no puede vaciar los badges por un error transitorio.
                const nextItems = (data && Array.isArray(data.items)) ? data.items : null;
                if (nextItems) {
                    itemsRef.current = nextItems;
                    setItems(nextItems);
                    // El payload manda: los ajustes optimistas ya cumplieron su función.
                    setFactOverrides({});
                    writeCachedItems(userId, nextItems);
                    // 🔴 `loaded` significa «tengo un payload», no «terminé de intentar». Marcarlo
                    // igual cuando el pedido FALLA convierte «no sé» en «no hay»: con el endpoint
                    // caído, `usePendingFactsFor` devolvía 0 para todos los clientes y el acto Revisa
                    // anunciaba «Por ahora no hay nada esperando tu ok» mientras el mazo, debajo,
                    // decía «No se pudieron cargar los hechos» (QA-knob2-10, 27/09). Un error no
                    // puede pintarse como un dato ausente. Los dos consumidores del flag —el
                    // notificador y la campana— ya se callan con `false`, que es lo correcto si no
                    // sabemos nada; el fail-soft de los items no cambia: lo que ya llegó se queda.
                    setLoaded(true);
                    setFailed(false);
                } else {
                    // Un 200 con un cuerpo que no es el esperado también es una falla: los items
                    // viejos se quedan (fail-soft), pero no hay que decir que se está cargando.
                    setFailed(true);
                }
                schedule();
            }).catch(() => {
                inFlightRef.current = false;
                if (cancelledRef.current) return;
                setFailed(true);
                schedule();
            });
        };

        fetchRef.current = fetchNow;
        fetchNow();

        const onWake = () => {
            if (document.hidden) {
                clearTimer();
                return;
            }
            fetchRef.current();
        };
        window.addEventListener('focus', onWake);
        document.addEventListener('visibilitychange', onWake);

        return () => {
            cancelledRef.current = true;
            clearTimer();
            window.removeEventListener('focus', onWake);
            document.removeEventListener('visibilitychange', onWake);
        };
    }, [enabled, userId, modulesLoaded]);

    const refresh = useCallback(() => {
        fetchRef.current();
    }, []);

    /** Corrección optimista del contador de un cliente (la usa la cola al aprobar/rechazar). */
    const overridePendingFacts = useCallback((clientId, count) => {
        setFactOverrides((prev) => ({...prev, [String(clientId)]: count}));
    }, []);

    const pendingFactsByClient = useMemo(() => {
        const byClient = {};
        items.forEach((item) => {
            if (item.kind === KIND_MEMORY_FACTS) byClient[String(item.client_id)] = item.count;
        });
        return {...byClient, ...factOverrides};
    }, [items, factOverrides]);

    const value = useMemo(() => ({
        items,
        loaded,
        failed,
        pendingFactsByClient,
        refresh,
        overridePendingFacts,
    }), [items, loaded, failed, pendingFactsByClient, refresh, overridePendingFacts]);

    return (
        <PendingWorkContext.Provider value={value}>
            {children}
        </PendingWorkContext.Provider>
    );
}

export const usePendingWork = () => useContext(PendingWorkContext);

/**
 * Hechos esperando revisión de UN cliente. `null` mientras no llegó el primer payload, para que un
 * badge pueda distinguir "todavía no sé" de "no hay nada" y no parpadee un 0.
 */
export function usePendingFactsFor(clientId) {
    const {pendingFactsByClient, loaded} = usePendingWork();
    if (!loaded || clientId === null || clientId === undefined) return null;
    return pendingFactsByClient[String(clientId)] || 0;
}
